import { PlusOutlined } from '@ant-design/icons'
import { endpoints, useDriverCredentials } from '@api/useDriverCredentials'
import { GraviButton, GraviGrid, NotificationMessage } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import type { AgGridReactProps } from 'ag-grid-react'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { getColumnDefs } from './columnDefs'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'driver-credentials-management'

export function CredentialManagement({ title, updateSetting, selectedCredential, setSelectedCredential }) {
  const [hasEdits, setHasEdits] = useState(false)
  const [rowData, setRowData] = useState([])
  const dirtyRef = useRef(null)
  const gridAPIRef = useRef(null)
  const { getCredentialsOverviewQuery, deleteCredentialMutation, upsertCredentialsMutation } = useDriverCredentials()
  const handleDelete = async (id) => await deleteCredentialMutation.mutateAsync(id)
  const queryClient = useQueryClient()
  const { data, isFetching } = getCredentialsOverviewQuery()
  const columnDefs = useMemo(() => getColumnDefs(handleDelete, hasEdits), [hasEdits])

  const agPropOverrides: AgGridReactProps = useMemo(
    () => ({
      getRowId: (row) => row.data.credential_id,
      suppressRowSelection: true,
      onCellEditingStopped: () => setHasEdits(true),
    }),
    [selectedCredential, setHasEdits]
  )
  const handleAddCredential = () => {
    setHasEdits(true)
    dirtyRef.current?.addDirtyRow({
      credential_id: `${crypto.randomUUID()}`,
      credential_name: null,
      days_valid: null,
      warning_threshold: null,
      dispatch_impact: null,
      $inserted: true,
    })
  }
  const refreshData = async () => {
    setHasEdits(false)
    await queryClient.resetQueries({
      queryKey: [endpoints.credential.overview],
    })
  }
  const handleSave = ({ dirtyChanges }) => {
    const fixed = dirtyChanges
      ?.map((change) => {
        const { $inserted, $id, undefined, $index, ...rest } = change
        if ($inserted) {
          delete rest.credential_id
        }
        return rest
      })
      .filter((change) => !!change.credential_name && !!change.dispatch_impact)
    if (!fixed.length) {
      NotificationMessage(
        'Please fill out all required fields',
        'Credential Name and Dispatch Impact are required.',
        true
      )
      return false
    }
    upsertCredentialsMutation.mutate(fixed)
    setHasEdits(false)
    return true
  }
  useEffect(() => {
    if (selectedCredential && gridAPIRef.current) {
      gridAPIRef.current?.getRowNode(selectedCredential)?.setSelected(true)
    }
  }, [selectedCredential, gridAPIRef.current])
  useEffect(() => {
    if (data?.rows) {
      setRowData(data.rows)
    }
  }, [data?.rows])

  return (
    <GraviGrid
      primaryKey={'credential_id'}
      isDirtyEdit
      dirtyChangesRef={dirtyRef}
      onDirtyChangeSave={handleSave}
      onDirtyChangeDiscard={refreshData}
      hideSaveDisplay
      loading={isFetching}
      rowData={rowData || []}
      externalRef={gridAPIRef}
      controlBarProps={{
        title,
        actionButtons: (
          <GraviButton icon={<PlusOutlined />} buttonText={'Add Credential'} onClick={handleAddCredential} success />
        ),
      }}
      columnDefs={columnDefs}
      agPropOverrides={agPropOverrides}
      storageKey={STORAGE_KEY}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}
